import { useEffect, useState } from "react";
import { Trophy, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const formatFJD = (n) => {
    if (!n || n <= 0) return "FJ$—";
    if (n >= 1_000_000) return `FJ$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
    if (n >= 1_000) return `FJ$${Math.round(n / 1_000)}k`;
    return `FJ$${n}`;
};

const labelFor = (status) => (status === "paid" ? "PAID" : "CLOSED");

export default function DealWins() {
    const [items, setItems] = useState([]);

    useEffect(() => {
        let mounted = true;
        const fetchWins = async () => {
            try {
                const { data } = await api.get("/public/deal-wins?limit=12");
                if (mounted) setItems(data?.items || []);
            } catch (_e) {
                /* fail silent — strip auto-hides if API unavailable */
            }
        };
        fetchWins();
        // refresh every 5 minutes — wins don't change often
        const interval = setInterval(fetchWins, 300_000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    if (!items.length) return null;

    // Duplicate the array so the marquee scroll loops seamlessly
    const looped = [...items, ...items];

    return (
        <section
            data-testid="deal-wins-ticker"
            className="relative bg-ocean-deep text-white overflow-hidden border-y border-ocean-deep/40 dark:border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
                <div className="flex items-center gap-2 flex-shrink-0 pr-4 border-r border-white/15">
                    <Trophy className="h-4 w-4 text-coral" />
                    <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-app font-semibold text-coral">
                        Recent wins
                    </span>
                </div>
                <div className="flex-1 overflow-hidden relative">
                    <div className="flex gap-8 animate-marquee whitespace-nowrap will-change-transform">
                        {looped.map((win, idx) => (
                            <div
                                key={`${idx}-${win.closed_on || ""}`}
                                data-testid={idx < items.length ? `deal-win-${idx}` : undefined}
                                className="flex items-center gap-2.5 text-sm font-app text-white/90 flex-shrink-0"
                            >
                                <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-coral/20 border border-coral/40 text-coral text-[10px] uppercase tracking-wider font-semibold">
                                    {labelFor(win.status)}
                                </span>
                                <span className="font-semibold text-white">{win.asset_label}</span>
                                {win.region && (
                                    <span className="inline-flex items-center gap-1 text-white/70">
                                        <MapPin className="h-3 w-3" />
                                        {win.region}
                                    </span>
                                )}
                                <span className="text-turquoise font-bold tabular-nums">{formatFJD(win.value_fjd_floored)}</span>
                                <span className="text-white/30">·</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
